// src/admin/pages/ImportTemplatesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  FileText, 
  Download, 
  FileSpreadsheet,
  File,
  Eye,
  Copy
} from 'lucide-react';

const ImportTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // const [editingTemplate, setEditingTemplate] = useState(null); // TODO: Implement edit functionality
  const [showPreview, setShowPreview] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    type: 'questions',
    file_format: 'csv',
    columns: [],
    newColumn: ''
  });

  // Template types
  const templateTypes = [
    { value: 'questions', label: 'Questions', description: 'Import questions for datarooms or capraise' },
    { value: 'shareholders', label: 'Shareholders', description: 'Import shareholder data for equity management' },
    { value: 'companies', label: 'Companies', description: 'Import company information' },
    { value: 'investors', label: 'Investors', description: 'Import investor data for capital raising' },
    { value: 'documents', label: 'Documents', description: 'Import document metadata' },
    { value: 'users', label: 'Users', description: 'Import user accounts and profiles' }
  ];

  const fileFormats = [
    { value: 'csv', label: 'CSV', description: 'Comma-separated values' },
    { value: 'xlsx', label: 'Excel', description: 'Microsoft Excel format' },
    { value: 'json', label: 'JSON', description: 'JavaScript Object Notation' }
  ];

  // Mock templates - in real implementation, these would come from an API
  const mockTemplates = [
    {
      id: 1,
      name: "Questions Import Template",
      description: "Template for importing questions into dataroom or capraise modules",
      type: "questions",
      file_format: "csv",
      columns: [
        { name: "question_text", required: true, description: "The question content" },
        { name: "category", required: true, description: "Question category" },
        { name: "module_type", required: true, description: "dataroom, capraise, or general" },
        { name: "priority", required: false, description: "High, Medium, or Low" },
        { name: "tags", required: false, description: "Comma-separated tags" }
      ],
      sample_data: [
        ["What is your revenue model?", "Financial", "capraise", "High", "revenue,financial"],
        ["How do you handle data security?", "Technical", "dataroom", "High", "security,compliance"],
        ["What is your competitive advantage?", "Business", "capraise", "Medium", "strategy,business"]
      ],
      created_at: "2024-01-15T10:00:00Z",
      usage_count: 25,
      is_system: true
    },
    {
      id: 2,
      name: "Shareholders Import Template",
      description: "Template for importing shareholder data into equity management",
      type: "shareholders",
      file_format: "csv",
      columns: [
        { name: "first_name", required: true, description: "Shareholder first name" },
        { name: "last_name", required: true, description: "Shareholder last name" },
        { name: "email", required: true, description: "Email address" },
        { name: "entity_type", required: true, description: "Individual, Company, Trust" },
        { name: "shareholder_type", required: true, description: "Founder, Investor, Employee, etc." },
        { name: "company_id", required: true, description: "Associated company ID" },
        { name: "phone", required: false, description: "Phone number" },
        { name: "address", required: false, description: "Street address" },
        { name: "city", required: false, description: "City" },
        { name: "state", required: false, description: "State/Province" },
        { name: "postcode", required: false, description: "Postal code" },
        { name: "country", required: false, description: "Country" }
      ],
      sample_data: [
        ["John", "Smith", "john.smith@example.com", "Individual", "Investor", "1", "+61 2 1234 5678", "123 Collins St", "Melbourne", "VIC", "3000", "Australia"],
        ["Jane", "Doe", "jane.doe@company.com", "Individual", "Founder", "1", "+61 3 9876 5432", "456 Bourke St", "Melbourne", "VIC", "3000", "Australia"]
      ],
      created_at: "2024-01-10T14:30:00Z",
      usage_count: 18,
      is_system: true
    },
    {
      id: 3,
      name: "Investors Import Template",
      description: "Template for importing investor data for capital raising campaigns",
      type: "investors",
      file_format: "xlsx",
      columns: [
        { name: "company_name", required: true, description: "Investor company name" },
        { name: "contact_name", required: true, description: "Primary contact person" },
        { name: "email", required: true, description: "Contact email" },
        { name: "phone", required: false, description: "Contact phone" },
        { name: "investment_size", required: false, description: "Typical investment size" },
        { name: "sectors", required: false, description: "Preferred investment sectors" },
        { name: "stage", required: false, description: "Preferred investment stage" },
        { name: "location", required: false, description: "Investor location" }
      ],
      sample_data: [
        ["ABC Ventures", "Sarah Johnson", "sarah@abcventures.com", "+1 555 123 4567", "$1M - $5M", "Fintech, SaaS", "Series A", "San Francisco"],
        ["XYZ Capital", "Michael Chen", "michael@xyzcapital.com", "+1 555 987 6543", "$500K - $2M", "Healthcare, Biotech", "Seed", "New York"]
      ],
      created_at: "2024-01-05T09:15:00Z",
      usage_count: 12,
      is_system: false
    },
    {
      id: 4,
      name: "Companies Import Template",
      description: "Template for importing company information into equity management",
      type: "companies",
      file_format: "csv",
      columns: [
        { name: "name", required: true, description: "Company name" },
        { name: "abn", required: false, description: "Australian Business Number" },
        { name: "acn", required: false, description: "Australian Company Number" },
        { name: "email", required: false, description: "Company email" },
        { name: "phone", required: false, description: "Company phone" },
        { name: "address", required: false, description: "Street address" },
        { name: "city", required: false, description: "City" },
        { name: "state", required: false, description: "State/Province" },
        { name: "postcode", required: false, description: "Postal code" },
        { name: "country", required: false, description: "Country" },
        { name: "incorporation_date", required: false, description: "Date of incorporation (YYYY-MM-DD)" }
      ],
      sample_data: [
        ["TechCorp Pty Ltd", "12 345 678 901", "123 456 789", "info@techcorp.com", "+61 2 9876 5432", "789 George St", "Sydney", "NSW", "2000", "Australia", "2020-01-15"],
        ["InnovateCo Ltd", "98 765 432 109", "987 654 321", "hello@innovateco.com", "+61 3 1234 5678", "456 Collins St", "Melbourne", "VIC", "3000", "Australia", "2019-06-20"]
      ],
      created_at: "2024-01-01T16:45:00Z",
      usage_count: 8,
      is_system: false
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setTemplates(mockTemplates);
      setLoading(false);
    }, 500);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTemplate = () => {
    const template = {
      id: Date.now(),
      name: newTemplate.name,
      description: newTemplate.description,
      type: newTemplate.type,
      file_format: newTemplate.file_format,
      columns: [...newTemplate.columns],
      created_at: new Date().toISOString(),
      usage_count: 0,
      is_system: false
    };
    
    setTemplates([...templates, template]);
    setNewTemplate({
      name: '',
      description: '',
      type: 'questions',
      file_format: 'csv',
      columns: [],
      newColumn: ''
    });
    setShowCreateModal(false);
  };

  // TODO: Implement edit functionality
  // const handleUpdateTemplate = (id, updatedData) => {
  //   setTemplates(templates.map(template => 
  //     template.id === id ? { ...template, ...updatedData } : template
  //   ));
  //   setEditingTemplate(null);
  // };

  const handleDeleteTemplate = (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    setTemplates(templates.filter(template => template.id !== id));
  };

  const handleDuplicateTemplate = (template) => {
    const duplicated = {
      ...template,
      id: Date.now(),
      name: `${template.name} (Copy)`,
      usage_count: 0,
      is_system: false
    };
    setTemplates([...templates, duplicated]);
  };

  const addColumn = () => {
    if (newTemplate.newColumn.trim()) {
      const columnName = newTemplate.newColumn.trim();
      setNewTemplate({
        ...newTemplate,
        columns: [...newTemplate.columns, { 
          name: columnName, 
          required: false, 
          description: '' 
        }],
        newColumn: ''
      });
    }
  };

  const removeColumn = (index) => {
    setNewTemplate({
      ...newTemplate,
      columns: newTemplate.columns.filter((_, i) => i !== index)
    });
  };

  const downloadTemplate = (template) => {
    // In a real implementation, this would generate and download the actual template file
    console.log('Downloading template:', template.name);
    // For now, we'll just show an alert
    alert(`Downloading ${template.name} template...`);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Import Templates</h2>
          <p className="text-gray-600 mt-1">Manage CSV and Excel templates for bulk data imports</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Format
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Downloads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          {template.file_format === 'csv' ? (
                            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                          ) : (
                            <File className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        <div className="text-sm text-gray-500">{template.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {templateTypes.find(t => t.value === template.type)?.label || template.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {template.file_format.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.columns.length} columns
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {template.usage_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadTemplate(template)}
                        className="text-green-600 hover:text-green-900"
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setShowPreview(template)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Preview"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicateTemplate(template)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      {!template.is_system && (
                        <>
                          <button
                            // onClick={() => setEditingTemplate(template)} // TODO: Implement edit functionality
                            className="text-blue-600 hover:text-blue-900 opacity-50 cursor-not-allowed"
                            disabled
                            title="Edit functionality coming soon"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new import template.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Import Template</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleCreateTemplate(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                    <input
                      type="text"
                      required
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={3}
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type *</label>
                      <select
                        required
                        value={newTemplate.type}
                        onChange={(e) => setNewTemplate({...newTemplate, type: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {templateTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Format *</label>
                      <select
                        required
                        value={newTemplate.file_format}
                        onChange={(e) => setNewTemplate({...newTemplate, file_format: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {fileFormats.map(format => (
                          <option key={format.value} value={format.value}>
                            {format.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="mt-2 space-y-2">
                      {newTemplate.columns.map((column, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{column.name}</span>
                          <button
                            type="button"
                            onClick={() => removeColumn(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newTemplate.newColumn}
                          onChange={(e) => setNewTemplate({...newTemplate, newColumn: e.target.value})}
                          placeholder="Add column name"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addColumn();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={addColumn}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Create Template
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Template Preview: {showPreview.name}</h3>
                <button
                  onClick={() => setShowPreview(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-sm text-gray-600">{showPreview.description}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Columns ({showPreview.columns.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Column Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {showPreview.columns.map((column, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{column.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {column.required ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Required
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Optional
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">{column.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {showPreview.sample_data && showPreview.sample_data.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Data</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {showPreview.columns.map((column, index) => (
                              <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {column.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {showPreview.sample_data.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-4 py-2 text-sm text-gray-900">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                  <span>Downloaded {showPreview.usage_count} times</span>
                  <span>Created {new Date(showPreview.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportTemplatesPage;
